if (!customElements.get('glyph-map')) {
  customElements.define(
    'glyph-map',
    class GlyphMap extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const configEl = this.querySelector('[data-glyph-map-config]');
        if (!configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Glyph map: invalid config', e);
          return;
        }

        this.styles = this.config.styles || [];
        if (!this.styles.length) return;

        this.initialized = true;
        this.cache = new Map();
        this.current = null;
        this.activeId = this.config.startGlyph || 'A';
        this.big = this.querySelector('[data-big]');
        this.code = this.querySelector('[data-code]');
        this.panel = this.querySelector('[data-panel]');
        this.stylesEl = this.querySelector('[data-styles]');
        this.countEl = this.querySelector('[data-count]');
        this.languagesEl = this.querySelector('[data-languages]');

        this.buildPills();
        this.bindPanel();

        const start = Math.max(
          0,
          this.styles.findIndex((s) => s.label.toLowerCase() === String(this.config.defaultStyle || '').toLowerCase())
        );

        // The outlines are only fetched when the section is about to scroll into view
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              this.select(start);
            },
            { rootMargin: '600px 0px' }
          );
          this.observer.observe(this);
        } else {
          this.select(start);
        }
      }

      buildPills() {
        // one style only (e.g. Bispo PRO): nothing to switch, so no pill
        this.stylesEl.hidden = this.styles.length < 2;
        this.pills = this.styles.map((style, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'glyph-map__style';
          button.textContent = style.label;
          button.setAttribute('aria-pressed', 'false');
          button.addEventListener('click', () => this.select(index));
          // fetch on hover so the switch feels instant
          button.addEventListener('pointerenter', () => this.load(index).catch(() => {}));
          this.stylesEl.appendChild(button);
          return button;
        });
      }

      bindPanel() {
        const cellOf = (event) => event.target.closest('.glyph-map__cell');
        const show = (event) => {
          const cell = cellOf(event);
          if (cell) this.showCell(cell);
        };
        this.panel.addEventListener('pointerover', show);
        this.panel.addEventListener('focusin', show);
        this.panel.addEventListener('click', show);
      }

      load(index) {
        if (!this.cache.has(index)) {
          const request = fetch(this.styles[index].url).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
          });
          this.cache.set(index, request);
          request.catch(() => this.cache.delete(index));
        }
        return this.cache.get(index);
      }

      async select(index) {
        this.pills.forEach((pill, i) => pill.setAttribute('aria-pressed', String(i === index)));
        this.current = index;
        this.setAttribute('aria-busy', 'true');
        try {
          const data = await this.load(index);
          if (this.current !== index) return; // a newer pick won
          this.render(data);
        } catch (e) {
          console.error('Glyph map: could not load glyphs', e);
          this.panel.textContent = this.config.errorText || 'The glyphs could not be loaded.';
        } finally {
          if (this.current === index) this.removeAttribute('aria-busy');
        }
      }

      render(data) {
        const { upm, asc, desc, cats } = data;
        let widest = 0;
        cats.forEach(([, glyphs]) => glyphs.forEach(([, advance]) => (widest = Math.max(widest, advance))));
        const boxWidth = widest + 40;

        this.style.setProperty('--gm-w', (boxWidth / upm).toFixed(4));
        this.style.setProperty('--gm-h', ((asc + desc) / upm).toFixed(4));

        const scrollTop = this.panel.scrollTop;
        let total = 0;
        const esc = GlyphMap.escape;
        const html = cats
          .map(([title, glyphs, note]) => {
            total += glyphs.length;
            const cells = glyphs
              .map(([char, advance, path, name]) => {
                // glyphs without a Unicode value are identified by their glyph name
                const id = char || name;
                const viewBox = `${((advance - boxWidth) / 2).toFixed(1)} ${-asc} ${boxWidth} ${asc + desc}`;
                return (
                  `<button type="button" class="glyph-map__cell" data-id="${esc(id)}" data-char="${esc(char)}" aria-label="${esc(id)}">` +
                  `<svg viewBox="${viewBox}" aria-hidden="true" focusable="false"><path transform="scale(1 -1)" d="${path}"/></svg></button>`
                );
              })
              .join('');
            const noteHtml = note ? `<p class="glyph-map__note">${esc(note)}</p>` : '';
            return `<h3 class="glyph-map__cat">${esc(title)}</h3>${noteHtml}<div class="glyph-map__grid">${cells}</div>`;
          })
          .join('');

        this.panel.innerHTML = html;
        this.panel.scrollTop = scrollTop;
        this.activeCell = null;
        if (this.countEl) this.countEl.textContent = `${total} ${this.config.countLabel || ''}`.trim();
        this.renderLanguages(data.langs);

        const cells = [...this.panel.querySelectorAll('.glyph-map__cell')];
        const keep = cells.find((cell) => cell.dataset.id === this.activeId) || cells[0];
        if (keep) this.showCell(keep);
      }

      renderLanguages(langs) {
        if (!this.languagesEl || !langs || !langs.count || this.languagesEl.childElementCount) return;
        const template = this.config.languagesText || 'Supports {count} languages.';
        const summary = document.createElement('p');
        summary.className = 'glyph-map__languages-summary';
        summary.textContent = template.replace('{count}', langs.count);

        const details = document.createElement('details');
        details.className = 'glyph-map__languages-list';
        const toggle = document.createElement('summary');
        toggle.textContent = this.config.languagesListLabel || 'See languages';
        const names = document.createElement('p');
        names.textContent = langs.names;
        details.append(toggle, names);

        this.languagesEl.append(summary, details);
        this.languagesEl.hidden = false;
      }

      showCell(cell) {
        if (this.activeCell === cell) return;
        this.activeCell?.classList.remove('is-active');
        this.activeCell = cell;
        cell.classList.add('is-active');
        this.activeId = cell.dataset.id;
        this.big.innerHTML = cell.innerHTML;
        const char = cell.dataset.char;
        this.code.textContent = char
          ? [...char].map((c) => `U+${c.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')}`).join(' ')
          : cell.dataset.id;
      }

      static escape(value) {
        return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
      }
    }
  );
}
